import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { c } from 'ttag';

import {
    AuthenticationStore,
    Button,
    ChallengeName,
    InputField,
    PasswordInput,
    useAuthentication,
    useFormErrors,
    useLoading,
    useNotifications,
} from '@freelancelabs/components';
import { requiredValidator } from '@freelancelabs/shared';

import { findSsoProvider } from './ssoDiscovery';

interface Props {
    onSubmit: (data: {
        challenge: ChallengeName | undefined;
        payload: {
            username: string;
            password: string;
        };
    }) => void;
    defaultUsername?: string;
    /**
     * Le formulaire est-il en mode SSO ? (PCG-304)
     *
     * **UN SEUL FORMULAIRE ET NON DEUX, et c'est ce que fait Cloudflare** : le champ e-mail
     * est le MEME objet, donc il garde sa valeur dans les deux sens sans que personne ait à
     * la faire voyager. Un second composant l'aurait remonté à la page pour le redescendre,
     * et deux champs auraient fini par diverger sur l'auto-remplissage, la validation ou le
     * `autoComplete`.
     *
     * Ce qui change tient en trois gestes : le mot de passe et son lien de réinitialisation
     * quittent l'écran, le bouton d'envoi change de nom, et l'envoi part vers la découverte
     * au lieu de Cognito.
     */
    ssoMode?: boolean;
    /** Remonte l'adresse pour que la bascule inverse la retrouve saisie. */
    onUsernameChange?: (username: string) => void;
}

const LoginForm = ({ defaultUsername = '', onSubmit, ssoMode = false, onUsernameChange }: Props) => {
    const { login, signInWithSso } = useAuthentication() as AuthenticationStore;
    const { validator, onFormSubmit } = useFormErrors();
    const [submitting, withSubmitting] = useLoading();
    const [username, setUsername] = useState(defaultUsername || '');
    const [password, setPassword] = useState('');
    /**
     * Le refus du SSO vit SOUS LE CHAMP et non en notification, contrairement aux erreurs de
     * mot de passe juste en dessous : il porte sur ce qui vient d'être saisi, et une
     * notification qui s'efface laisserait la personne devant un champ rempli sans savoir
     * pourquoi rien ne s'est passé.
     */
    const [ssoRefusal, setSsoRefusal] = useState<string | null>(null);

    const { createNotification } = useNotifications();

    /**
     * La moitie actionnable de la phrase « Vous avez oublie votre mot de passe ? ».
     *
     * Attention, UNE VARIABLE ET NON UN APPEL DE FONCTION dans le gabarit `jt` :
     * `babel-plugin-ttag` refuse `${'${'}fabrique()}` a l'extraction, avec une erreur qui
     * n'apparait que sous les centaines de lignes de bruit de `i18n:update`. Les quatre
     * autres `jt` du depot passent tous un identifiant, et c'est pour cette raison.
     *
     * La `key` evite la plainte de React sur une liste sans cle : le gabarit rend un tableau.
     */
    const resetPasswordLink = (
        <Link key="reset-password" to="/reset-password" className="link font-medium">
            {c('Link').t`password`}
        </Link>
    );

    /**
     * L'envoi en mode SSO.
     *
     * **Attention, LE REFUS S'ÉCRIT, ET C'EST LE SEUL ÉCART VOLONTAIRE AVEC CLOUDFLARE.**
     * Observé le 2026-08-28 : quand aucun fournisseur ne répond, sa page revient au
     * formulaire normal **sans un mot**, mot de passe réactivé et bouton redevenu
     * « Sign in ». C'est un refus rendu comme une absence, et la personne refait le même
     * geste. Ici l'écran reste en mode SSO et dit ce qui ne va pas.
     */
    const submitSso = () => {
        setSsoRefusal(null);
        onUsernameChange?.(username);

        const run = async () => {
            const provider = await findSsoProvider(username);
            if (!provider) {
                setSsoRefusal(
                    c('Sso').t`No single sign-on is set up for this email domain. Sign in with your password instead.`
                );
                return;
            }
            /*
             * La page est REMPLACÉE par la redirection : rien ne s'exécute après, et le
             * bouton reste donc en chargement jusqu'à ce que le navigateur parte. Le rendre
             * à son état normal ici donnerait une page qui a l'air de n'avoir rien fait
             * pendant la seconde où elle prépare la navigation.
             */
            await signInWithSso(provider);
        };

        withSubmitting(run()).catch(() => {
            /*
             * Le seul échec atteignable est « l'OAuth n'est pas monté sur cette
             * application », que `signInWithSso` lève plutôt que de ne rien faire. On le dit
             * en toutes lettres : un bouton muet se lit comme une panne du réseau.
             */
            setSsoRefusal(c('Sso').t`Single sign-on is unavailable right now. Sign in with your password instead.`);
        });
    };

    const submitPassword = () => {
        const run = async () => {
            const challenge = await login({ email: username, password });
            return onSubmit({
                challenge,
                payload: {
                    username,
                    password,
                },
            });
        };

        withSubmitting(run()).catch((e) => {
            const message = e.message;
            switch (message) {
                case 'Incorrect username or password.':
                    createNotification({
                        type: 'danger',
                        text: c('Login Error').t`Incorrect username or password.`,
                    });
                    break;
                case 'Password attempts exceeded':
                    createNotification({
                        type: 'danger',
                        text: c('Login Error').t`Password attempts exceeded`,
                    });
                    break;
                default:
                    createNotification({
                        type: 'danger',
                        text: c('Login Error').t`Unknown error`,
                    });
            }
        });
    };

    const handleSubmit = () => {
        if (submitting || !onFormSubmit()) {
            return;
        }
        if (ssoMode) {
            submitSso();
            return;
        }
        submitPassword();
    };

    return (
        <form
            name="loginForm"
            className="flex flex-col"
            onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
            }}
            method="post"
        >
            <InputField
                label={c('Info').t`Email address`}
                id="username"
                type="email"
                error={validator([requiredValidator(username)])}
                disableChange={submitting}
                autoComplete="username"
                value={username}
                onValue={(value: string) => {
                    setUsername(value);
                    // Le refus porte sur l'adresse refusée : il tombe dès qu'elle change,
                    // sinon il contredit ce que la personne est en train de corriger.
                    setSsoRefusal(null);
                }}
            />
            {/*
                LE MOT DE PASSE ET SON LIEN QUITTENT L'ÉCRAN EN MODE SSO, là où Cloudflare les
                laisse dans le DOM, désactivés. Un champ grisé qu'aucun geste de l'écran ne
                rouvre est un contrôle qui n'apprend rien : la règle du dépôt sur le choix
                désactivé vise un choix PARMI D'AUTRES qu'on pourrait croire absent, ce qui
                n'est pas le cas ici, la bascule inverse étant à l'écran et nommée.
            */}
            {!ssoMode && (
                <>
                    <InputField
                        label={c('Info').t`Password`}
                        id="password"
                        as={PasswordInput}
                        error={validator([requiredValidator(password)])}
                        disableChange={submitting}
                        autoComplete="current-password"
                        value={password}
                        onValue={setPassword}
                    />
                </>
            )}
            {/*
                Le refus du SSO porte le MEME habillage que le message d'erreur d'un champ,
                et pour la meme raison : c'est la meme nature de phrase, une correction posee
                sous ce qu'elle corrige. Deux rouges et deux graisses sur un meme formulaire
                se liraient comme deux degres de gravite qui n'existent pas.

                `text-xs` et non `text-sm` : la taille du message de champ juste au-dessus.
            */}
            {ssoRefusal && (
                <p role="alert" className="text-xs text-danger-700 pb-2">
                    {ssoRefusal}
                </p>
            )}
            <Button size="large" type="submit" fullWidth loading={submitting} className="mt-4">
                {ssoMode ? c('Sso').t`Continue with SSO` : c('Action').t`Login`}
            </Button>
            {/*
                SOUS LE BOUTON ET NON AU-DESSUS, en phrase, comme Cloudflare (mesure du
                2026-08-28 : « Vous avez oublié votre e-mail ou votre mot de passe ? », 14 px,
                texte gris et la partie actionnable en bleu, sous le formulaire).

                Il etait au-dessus du bouton, aligne a droite, entre le dernier champ et
                l'action principale : donc sur le chemin de l'oeil de quelqu'un qui sait son
                mot de passe. En dessous, il ne s'adresse qu'a qui vient d'echouer.

                Attention, LA PHRASE EST A TROU ET C'EST VOULU : seul « mot de passe » est
                actionnable, le reste est du texte. `jt` porte le trou jusqu'au catalogue, donc
                une langue qui deplace le mot deplace le lien avec.

                Absent en mode SSO, ou il n'y a pas de mot de passe a oublier. Cloudflare
                retire la meme ligne au meme moment.
            */}
            {!ssoMode && (
                <p className="text-sm text-text-weak text-center mt-4">
                    {c('Login').jt`Forgot your ${resetPasswordLink}?`}
                </p>
            )}
        </form>
    );
};

export default LoginForm;
